import { IBatchjob } from "@mctypes/batchjob.types";
import { Box, Card, Flex, Stack, Text, Title } from "@mantine/core";
import React from "react";
import { useHistory } from "react-router-dom";
import { AccessView } from "../../../components/AccessComp/AccessComp";
import { CreateNewButton, MCLoader, SegmentedToggle } from "../../../components";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import NewBatchJobModal from "./NewBatchJobModal";
import { doCreateBatchjob } from "./batchJobAPI";
import { BATCHJOB_STATUSES, BatchjobStatusEnum, BatchJobTemplateEnum } from "./batchJobConstants";
import { fetchAndSetBatchJobsThunk, fetchTemplatesThunk, resetBatchJobApp } from "./batchJobSlice";

const rtf = new Intl.RelativeTimeFormat("nb", { numeric: "auto" });

const formatFromNow = (date: string | Date): string => {
  const diff = Date.now() - new Date(date).getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (years > 0) return rtf.format(-years, "year");
  if (months > 0) return rtf.format(-months, "month");
  if (days > 0) return rtf.format(-days, "day");
  if (hours > 0) return rtf.format(-hours, "hour");
  if (minutes > 0) return rtf.format(-minutes, "minute");
  return rtf.format(-seconds, "second");
};

const BatchJobApp: React.FC = () => {
  const [newBatchJobModalOpen, setNewBatchJobModalOpen] = React.useState<boolean>(false);
  const [activeStatus, setActiveStatus] = React.useState<BatchjobStatusEnum>(BatchjobStatusEnum.ACTIVE);

  const project = useAppSelector((state) => state.adminReducer.project);
  const batchJobs = useAppSelector((state) => state.batchJobReducer.batchJobs);
  const templates = useAppSelector((state) => state.batchJobReducer.templates);
  const convertedTemplates = templates.map((template) => {
    return {
      value: template,
      label: template,
    };
  });

  const history = useHistory();

  const dispatch = useAppDispatch();

  const statusCounts = React.useMemo(() => {
    return (batchJobs ?? []).reduce<Record<string, number>>((acc, board) => {
      acc[board.status] = (acc[board.status] || 0) + 1;
      return acc;
    }, {});
  }, [batchJobs]);

  React.useEffect(() => {
    init();
  }, []);

  const init = async () => {
    dispatch(resetBatchJobApp());
    await dispatch(fetchAndSetBatchJobsThunk());
    await dispatch(fetchTemplatesThunk());
  };

  const createBatchJob = async (name: string, template: BatchJobTemplateEnum) => {
    const createdBoard = await doCreateBatchjob(project?._id, name, template);
    goToTemplateBoard(createdBoard);
  };

  const goToTemplateBoard = (board: IBatchjob.IBatchJobBoardPopulated) => {
    const path = `/project/${project?._id}/batchjob/${board.template.toLowerCase()}/${board._id}/settings`;
    history.push(path);
  };

  return (
    <Flex direction="column" align="center" h="100%" pt="2rem" style={{ overflow: "auto" }} w="100%">
      {newBatchJobModalOpen === true && (
        <NewBatchJobModal
          templates={convertedTemplates}
          closeModal={() => setNewBatchJobModalOpen(false)}
          onCreate={createBatchJob}
        />
      )}
      <Box maw="50%" w="100%">
        {batchJobs != null && (
          <Stack gap={0}>
            <Flex align="center" mb="xs" style={{ position: "relative" }}>
              <Box pos="absolute" right="100%" mr="0.5rem">
                <CreateNewButton onClick={() => setNewBatchJobModalOpen(true)} size="sm" />
              </Box>
              <SegmentedToggle
                fullWidth
                value={activeStatus}
                onChange={(status) => setActiveStatus(status as BatchjobStatusEnum)}
                options={BATCHJOB_STATUSES.map((status) => ({
                  value: status.value,
                  label: status.label,
                  count: statusCounts[status.value] || 0,
                }))}
              />
            </Flex>

            <Box p="0.25rem">
              {batchJobs
                .filter((registerBoard) => registerBoard.status === activeStatus)
                .map((registerBoard) => (
                  <Card key={registerBoard._id} withBorder radius="sm" style={{ padding: "0" }}>
                    <Box p="0.4rem">
                      <Flex style={{ alignItems: "center" }}>
                        <Title
                          order={5}
                          style={{
                            flexGrow: 1,
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            marginBottom: "0.2rem",
                            cursor: "pointer",
                            fontWeight: "normal",
                            fontSize: "1rem",
                            maxWidth: "100%",
                            width: "max-content",
                          }}
                          onClick={() => goToTemplateBoard(registerBoard)}
                        >
                          {registerBoard.name}
                        </Title>

                        {registerBoard.progress != null && (
                          <div
                            style={{
                              width: "25%",
                              display: "flex",
                              overflow: "hidden",
                              borderRadius: "999px",
                              height: "0.5rem",
                              background: "#e9ecef",
                            }}
                          >
                            {registerBoard.progress.map((progress, index) => (
                              <div
                                key={index}
                                style={{
                                  width: `${progress.percentage}%`,
                                  backgroundColor: progress.color,
                                  height: "100%",
                                }}
                              />
                            ))}
                          </div>
                        )}
                      </Flex>

                      {registerBoard.createdFromTemplate != null && (
                        <Text style={{ fontSize: "0.8rem", margin: "0px" }}>
                          Mal: {registerBoard.createdFromTemplate}
                        </Text>
                      )}

                      <Text style={{ fontSize: "0.8rem", margin: "0px" }}>
                        Opprettet {formatFromNow(registerBoard.createdAt)} av {registerBoard.createdBy?.name}
                      </Text>

                      {registerBoard.access != null && <AccessView access={registerBoard.access} />}
                    </Box>
                  </Card>
                ))}
            </Box>

            {batchJobs.length === 0 && <p>Her var det tomt. Trykk på plussknappen for å opprette ny.</p>}
          </Stack>
        )}

        {batchJobs == null && <MCLoader loading />}
      </Box>
    </Flex>
  );
};

export default BatchJobApp;
